import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Dialog } from '@packages/shared/src/components/Dialog/Dialog';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useState } from 'react';
import { EcoLabelItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/EcoLabelItemFragmentFragmentDoc';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking';

/* GraphQL */ `
  fragment EcoLabelItemFragment on EcoLabel {
    iconUrl
    text
    serviceLayerId
    license
  }
`;

export type EcoLabelProps = {
  ecoLabelData: FragmentType<typeof EcoLabelItemFragmentFragmentDoc>;
  /**
   * If `ecoLabelData['text']` is `null`, this fallback is used instead.
   */
  fallbackHeadline?: string;
};

/**
 * <p>This component renders one eco-attribute of a product</p>
 * <i>(e.G.: "Bio Baumwolle", "Grüner Knopf" or "GOTS organic").</i>
 * */
export const EcoLabelItem = ({ fallbackHeadline, ecoLabelData }: EcoLabelProps) => {
  const { iconUrl, license, serviceLayerId, text } = getFragmentData(
    EcoLabelItemFragmentFragmentDoc,
    ecoLabelData,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { trackServiceLayerClick } = useServiceLayerTracking();

  // if iconUrl, license and text is missing we can not render the component
  // because it would have no content to display
  if (!iconUrl && !license && !text) {
    return null;
  }

  const handleClick = () => {
    trackServiceLayerClick(serviceLayerId!);
    setIsModalOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center gap-1.5 text-left"
        onClick={serviceLayerId ? handleClick : undefined}
        disabled={!serviceLayerId}
      >
        {iconUrl && (
          <Image
            width={52}
            height={52}
            alt={text || ''}
            src={iconUrl}
            unoptimized
            style={{ objectFit: 'contain' }}
          />
        )}
        <span className="flex flex-col">
          {text && <span className="text-size-body2 underline">{text}</span>}
          {license && (
            <span className="text-size-body3 text-color-text-darktransparent">{license}</span>
          )}
        </span>
      </button>
      {serviceLayerId && (
        <Dialog
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          title={text || fallbackHeadline || ''}
          dialogContent={<ServiceLayer serviceLayerId={serviceLayerId} />}
          displayTrackingProps={{ category: 'dv-service-layer-eco-label-item' }}
        />
      )}
    </>
  );
};
